import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { useParams, useNavigate, NavigateFunction, Params } from 'react-router-dom';

import { Team } from '../../../../../../types/entity.types';

import Modal from '../../../../../common/modal/Modal';
import TextInput from '../../../../../common/input/text-input/TextInput';
import Button from '../../../../../common/button/Button';
import DeleteTeamConfirmationModal from '../delete-team-confirmation-modal/DeleteTeamConfirmationModal';

import '../team-modals.scss';

type Props = {
  show: boolean;
  close: () => void;
  team?: Team;
  updateTeam: (teamToSave: Team, close: () => void) => void;
  deleteTeam: (deleteTeam: () => void) => void;
};

const EditTeamModal = ({ show, close, team, updateTeam, deleteTeam }: Props): ReactElement => {
  const [name, setName] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const { teamExternalReference }: Readonly<Params> = useParams();

  const navigate: NavigateFunction = useNavigate();

  useEffect((): void => {
    if (team) {
      setName(team.name ?? '');
      return;
    }
  }, [show, team]);

  if (!show) return <React.Fragment />;

  const handleDeleteClick = (): void => {
    deleteTeam((): void => {
      navigate(-1);
      setShowConfirmation(false);
      close();
    });
  };

  const handleSaveClick = (): void => {
    const teamToSave: Team = {
      teamName: name,
      teamExternalReference: teamExternalReference
    };

    updateTeam(teamToSave, close);
  };

  if (showConfirmation) {
    return (
      <DeleteTeamConfirmationModal
        show={showConfirmation}
        close={(): void => setShowConfirmation(false)}
        team={team}
        onDeleteClick={handleDeleteClick}
      />
    );
  }

  return (
    <Modal show={show}>
      <section className="team-modals">
        <h1 className="team-modals__header">Edit Team</h1>
        <div className="edit-team-modal__inputs">
          <TextInput
            label="Team Name:"
            name="name"
            value={name}
            onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setName(target.value)}
            autoFocus
          />
        </div>
        <div className="team-modals__button-container">
          <Button text="Cancel" onClick={close} />
          <Button text="Delete" onClick={(): void => setShowConfirmation(true)} />
          <Button text="Save" onClick={handleSaveClick} disabled={!name} />
        </div>
      </section>
    </Modal>
  );
};

export default EditTeamModal;
